import React, { useEffect, useState } from 'react';
import {
  Box,
  GlobalStyles,
  Card,
  CardContent,
  Typography,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';

// Third party libraries
import io from 'socket.io-client';

// Utils
import { getUserToken } from '~/utils/auth';
import { setItem } from '~/utils/local-storage';

import './styles.scss';
import DragDropUpload from './DragDropUpload';
import MappedContacts from './MappedContacts';
import ProgressBar from './ProgressBar';

const ImportContacts = ({
  view,
  onFileSelectedChange,
  processFileSignal,
  onSubStepChange,
  onProgressStateChange,
  triggerProgressImportSignal,
  triggerProgressBackSignal,
  parentBackSignal,
  persistedSelectedFile,
  setPersistedSelectedFile,
  persistedTagsData,
  setPersistedTagsData,
}) => {
  const [localSelectedFile, setLocalSelectedFile] = useState([]);
  const [requestId, setRequestId] = useState(null);
  const [processCsv, setProcessCsv] = useState(null);

  // Tags data state
  const [localTagsData, setLocalTagsData] = useState({
    selectedTags: [],
    selectedTagIds: [],
    isDncList: false,
  });

  const selectedFile =
    persistedSelectedFile !== undefined ? persistedSelectedFile : localSelectedFile;
  const setSelectedFile =
    typeof setPersistedSelectedFile === 'function'
      ? setPersistedSelectedFile
      : setLocalSelectedFile;

  const tagsData = persistedTagsData !== undefined ? persistedTagsData : localTagsData;
  const setTagsData =
    typeof setPersistedTagsData === 'function' ? setPersistedTagsData : setLocalTagsData;

  const handleBackToUpload = () => {
    setActiveStep(0);
  };
  const handleBackToMap = () => {
    setActiveStep(1);
  };

  // Handle tags change from DragDropUpload
  const handleTagsChange = (newTagsData) => {
    console.log('ImportContacts - Received tags data from DragDropUpload:', newTagsData);
    setTagsData(newTagsData);
  };

  // Handle import completion
  const handleImportComplete = (importData) => {
    // Update tags data with any additional data from import
    if (importData) {
      setTagsData((prev) => ({
        ...prev,
        ...importData,
      }));
    }
    setActiveStep(2);
  };
  const CustomStepIcon = (props) => {
    const { active, icon } = props;

    return (
      <Box
        sx={{
          width: 28,
          height: 28,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: 'Inter',
          fontSize: 14,
          fontWeight: 400,
          lineHeight: '20px',
          border: active ? 'none' : '1px solid rgba(0, 0, 0, 0.24)',
          color: active ? '#fff' : '#000',
          backgroundColor: active ? '#F26722' : '#fff',
        }}
      >
        {icon}
      </Box>
    );
  };

  const [activeStep, setActiveStep] = useState(0);
  const steps = ['Upload', 'Map and Process', 'Finish'];

  // Notify parent of sub-step changes (Orders context)
  useEffect(() => {
    if (typeof onSubStepChange === 'function') {
      onSubStepChange(activeStep);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStep]);

  // Listen for parent back signal (Orders context)
  useEffect(() => {
    if (parentBackSignal === undefined || parentBackSignal === null) return;
    // If in mapping step, go back to upload
    // For progress step, parent should use triggerProgressBackSignal
    if (activeStep === 1) {
      handleBackToUpload();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parentBackSignal]);

  // Debug logging for tags data
  useEffect(() => {
    console.log('ImportContacts - Current tags data:', tagsData);
  }, [tagsData]);

  // Socket connection effect for real-time progress updates
  useEffect(() => {
    // Connect to Socket.IO server
    const socketConnection = io(import.meta.env.VITE_APP_BASE_URL, {
      transports: ['websocket'],
      query: {
        token: getUserToken(),
      },
    });

    if (requestId) {
      // Listen for import progress updates
      socketConnection.on(`import-contacts-progress-${requestId}`, (data) => {
        setProcessCsv((prev) => ({
          ...prev,
          ...data,
        }));
        // Store progress in localStorage for persistence
        setItem('csvProcessing', JSON.stringify(data));
      });
    }

    // Clean up the socket connection when the component unmounts or requestId changes
    return () => {
      socketConnection.disconnect();
    };
  }, [requestId]);

  return (
    <>
      {/* Change body background only for this component */}
      <GlobalStyles
        styles={{
          body: { backgroundColor: '#F9FAFB' },
        }}
      />

      <Box
        className={`olcWrapper ${view === 'upload_list' ? 'ordersImportWrapper' : 'standaloneImportWrapper'}`}
      >
        <Box
          className={`importModuleWrapper ${activeStep === 0 ? 'tabsScreenCardDynamicLength' : 'tabsScreenCard'} ${
            view === 'upload_list' ? 'ordersImportCard' : ''
          } ${view !== 'upload_list' ? 'standaloneImportCard' : ''}`}
        >
          <CardContent
            className="importWizardContent"
            sx={{
              py: 0,
              px: 3,
              '&:last-child': {
                paddingBottom: 0, // or whatever you need
              },
            }}
          >
            {!view && (
              <Box className="importWizardHeader">
                <Typography className="importWizardTitle" variant="h6" sx={{ fontWeight: 600 }}>
                  Import Contacts
                </Typography>

                <Stepper activeStep={activeStep} className="importWizardStepper">
                  {steps.map((label, index) => (
                    <Step key={label}>
                      <StepLabel
                        StepIconComponent={CustomStepIcon}
                        className={activeStep === index ? 'active' : ''}
                      >
                        {label}
                      </StepLabel>
                    </Step>
                  ))}
                </Stepper>
              </Box>
            )}

            {activeStep === 0 && (
              <DragDropUpload
                updateSelectedFile={(val) => setSelectedFile(val)}
                updateActiveTabe={(val) => setActiveStep(val)}
                onTagsChange={handleTagsChange}
                isOrdersContext={view === 'upload_list'}
                onFileSelectedChange={onFileSelectedChange}
                processFileSignal={processFileSignal}
                initialSelectedTags={tagsData.selectedTags}
                initialSelectedTagIds={tagsData.selectedTagIds}
                initialIsDncList={tagsData.isDncList}
                initialSelectedFile={selectedFile}
              />
            )}
            {activeStep === 1 && (
              <MappedContacts
                selectedFile={selectedFile}
                setRequestId={setRequestId}
                setProcessCsv={setProcessCsv}
                onImportComplete={handleImportComplete}
                onBack={handleBackToUpload}
                selectedTags={tagsData.selectedTags}
                selectedTagIds={tagsData.selectedTagIds}
                isDncList={tagsData.isDncList}
                isOrdersContext={view === 'upload_list'}
                processFileSignal={processFileSignal}
                onProgressStateChange={onProgressStateChange}
              />
            )}
            {activeStep === 2 && (
              <ProgressBar
                onBack={handleBackToUpload}
                onBackToMap={handleBackToMap}
                value={processCsv?.current || 0}
                requestId={requestId}
                processCsv={processCsv}
                selectedTagIds={tagsData.selectedTagIds}
                isDncList={tagsData.isDncList}
                isOrdersContext={view === 'upload_list'}
                onProgressStateChange={onProgressStateChange}
                triggerImportSignal={triggerProgressImportSignal}
                triggerBackToMapSignal={triggerProgressBackSignal}
              />
            )}
          </CardContent>
        </Box>
      </Box>
    </>
  );
};

export default ImportContacts;
